import { captureConversationAccess } from 'dashboard/permissionSets/conversationReads';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';

const MAX_DISCONNECT_SECONDS = 10800;

class ReconnectService {
  constructor(store) {
    this.store = store;
    this.disconnectTime = null;
    this.reconnectAttempt = null;

    this.setupEventListeners();
  }

  disconnect = () => {
    this.reconnectAttempt = null;
    this.removeEventListeners();
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
    }
  };

  revalidateCaches = async () => {
    const isCurrent = captureConversationAccess({
      rootState: this.store.state,
      rootGetters: this.store.getters,
    });
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    if (!isCurrent()) return;
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  onDisconnect = () => {
    this.reconnectAttempt = null;
    this.disconnectTime = new Date();
  };

  onReconnect = async () => {
    const attempt = {};
    this.reconnectAttempt = attempt;
    const accessIsCurrent = await this.store.dispatch(
      'permissionSets/revalidateOnReconnect',
      { isConnected: () => this.reconnectAttempt === attempt }
    );
    const isCurrent = () =>
      this.reconnectAttempt === attempt && accessIsCurrent && accessIsCurrent();
    if (!isCurrent()) return;
    await this.store.dispatch('accounts/get', { silent: true });
    if (!isCurrent()) return;
    await this.revalidateCaches();
    if (!isCurrent()) return;
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
  };
}

export default ReconnectService;
